<script lang="ts">
  import type { AppController } from '../../app/controller.svelte';
  import { ACTIONS, chordOf, defaultKeymap, describeBinding } from '../../core/actions';
  import { describeTrigger } from '../../core/midi';
  import {
    CATEGORIES,
    SETTING_DEFS,
    searchSettings,
    settingParents,
    settingVisible,
    type SettingDef,
    type Settings,
  } from '../../shared/settings';
  import AccordionSection from '../components/AccordionSection.svelte';
  import TermsText from '../components/TermsText.svelte';
  import Icon from '../components/Icon.svelte';

  let { ctl }: { ctl: AppController } = $props();
  let query = $state('');
  let advanced = $state(false);
  const searching = $derived(query.trim().length > 0);
  // Dependent settings are hidden while their parent is off (a search still finds them).
  const defs = $derived(
    searchSettings(query).filter(
      (d) => (advanced || !d.advanced) && (searching || settingVisible(d, ctl.settings)),
    ),
  );
  /** Accordion: one section open at a time (all matches open while searching). */
  const isOpen = (id: string) => searching || ctl.settingsOpen === id;
  const toggle = (id: string) => (ctl.settingsOpen = ctl.settingsOpen === id ? null : id);
  /** The extra (non-registry) sections show while searching only if their title matches. */
  const extraShown = (title: string) =>
    !searching || title.toLowerCase().includes(query.trim().toLowerCase());
  const labelOf = (key: keyof Settings) => SETTING_DEFS.find((x) => x.key === key)?.label ?? key;
  const needs = (d: SettingDef) =>
    settingVisible(d, ctl.settings)
      ? null
      : `Only used when “${settingParents(d).map(labelOf).join('” or “')}” is on.`;
  const byCategory = $derived(
    CATEGORIES.map((c) => ({ c, defs: defs.filter((d) => d.category === c) })).filter(
      (g) => g.defs.length,
    ),
  );
  let capturing = $state<{ action: string; arg?: number } | null>(null);
  let captureError = $state<string | null>(null);
  let profileInput = $state<HTMLInputElement>();

  function value(d: SettingDef): Settings[keyof Settings] {
    return ctl.settings[d.key];
  }

  function captureKey(e: KeyboardEvent) {
    if (!capturing) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.key === 'Escape') {
      capturing = null;
      return;
    }
    const chord = chordOf(e);
    if (!chord) return;
    const err = ctl.bindKey(chord, {
      action: capturing.action,
      ...(capturing.arg !== undefined ? { arg: capturing.arg } : {}),
    });
    captureError = err;
    if (!err) capturing = null;
  }

  const bindings = $derived(
    ACTIONS.flatMap((a) =>
      a.slot
        ? Array.from({ length: 10 }, (_, i) => ({
            action: a.id,
            arg: i as number | undefined,
            label: describeBinding({ action: a.id, arg: i }),
          }))
        : [{ action: a.id, arg: undefined as number | undefined, label: a.label }],
    ),
  );
  const chordsOf = (action: string, arg?: number) =>
    Object.entries(ctl.keymap)
      .filter(([, b]) => b.action === action && (arg === undefined || b.arg === arg))
      .map(([c]) => c);
</script>

<svelte:window onkeydowncapture={captureKey} />

<section class="sec" data-tour="settings-search">
  <input
    type="search"
    class="search"
    placeholder="Search settings"
    bind:value={query}
    data-testid="settings-search"
    aria-label="Search settings"
  />
  <label class="row small muted"
    ><input type="checkbox" bind:checked={advanced} /> Show advanced</label
  >
</section>

{#each byCategory as g (g.c)}
  <AccordionSection
    id={g.c.toLowerCase().replace(/[^a-z]+/g, '-')}
    title={g.c}
    meta={String(g.defs.length)}
    open={isOpen(g.c)}
    ontoggle={() => toggle(g.c)}
  >
    {#each g.defs as d (d.key)}
      {@const src = ctl.settingsStore.sourceOf(d.key, ctl.scene)}
      {@const missing = needs(d)}
      <div class="setting" class:child={!!d.parent} data-testid={`setting-${d.key}`}>
        <div class="setting-head">
          <span class="label">{d.label}</span>
          {#if src === 'preset'}<span class="chip accent" title="Set by the current preset"
              >preset</span
            >{/if}
          {#if d.reload}<span class="chip warn">reload</span>{/if}
          {#if d.kind === 'bool'}
            <input
              type="checkbox"
              checked={value(d) === true}
              aria-label={d.label}
              onchange={(e) => ctl.setSetting(d.key, e.currentTarget.checked as never)}
            />
          {:else if d.kind === 'select'}
            <select
              value={String(value(d))}
              aria-label={d.label}
              onchange={(e) => {
                const raw = e.currentTarget.value;
                const opt = d.options.find((o) => String(o.value) === raw);
                if (opt) ctl.setSetting(d.key, opt.value as never);
              }}
            >
              {#each d.options as o (o.value)}<option value={String(o.value)}>{o.label}</option
                >{/each}
            </select>
          {/if}
        </div>
        {#if d.kind === 'number'}
          <div class="num">
            <input
              type="range"
              min={d.min}
              max={d.max}
              step={d.step}
              value={value(d) as number}
              aria-label={d.label}
              oninput={(e) => ctl.setSetting(d.key, Number(e.currentTarget.value) as never)}
            />
            <span class="v"
              >{(value(d) as number).toFixed(d.step >= 1 ? 0 : 2)}{d.unit ? ` ${d.unit}` : ''}</span
            >
          </div>
        {/if}
        <p class="hint">{d.description}</p>
        {#if missing}<p class="hint needs">{missing}</p>{/if}
      </div>
    {/each}
  </AccordionSection>
{/each}

{#if extraShown('Keyboard shortcuts')}
  <AccordionSection
    id="shortcuts"
    title="Keyboard shortcuts"
    open={isOpen('Keyboard shortcuts')}
    ontoggle={() => toggle('Keyboard shortcuts')}
  >
    <div class="row reset">
      <button class="link" onclick={() => ctl.setKeymap(defaultKeymap())}>Reset to defaults</button>
    </div>
    {#if capturing}<p class="hint capture">
        Press a key combination… (Esc cancels){#if captureError}
          · {captureError}{/if}
      </p>{/if}
    <div class="keys">
      {#each bindings as b (`${b.action}:${b.arg}`)}
        {@const chords = chordsOf(b.action, b.arg)}
        <div class="key-row">
          <span class="small grow">{b.label}</span>
          {#each chords as c (c)}
            <button class="kbd" title="Remove" onclick={() => ctl.bindKey(c, null)}
              ><kbd>{c}</kbd></button
            >
          {/each}
          <button
            class="icon-btn"
            title="Add shortcut"
            onclick={() => {
              capturing = { action: b.action, ...(b.arg !== undefined ? { arg: b.arg } : {}) };
              captureError = null;
            }}><Icon name="plus" /></button
          >
        </div>
      {/each}
    </div>
  </AccordionSection>
{/if}

{#if extraShown('MIDI')}
  <AccordionSection id="midi" title="MIDI" open={isOpen('MIDI')} ontoggle={() => toggle('MIDI')}>
    {#if !ctl.midi.supported}
      <p class="hint">This browser has no Web MIDI (Safari). Use Chrome, Edge or Firefox.</p>
    {:else if ctl.midiVersion >= 0 && !ctl.midi.enabled}
      <button onclick={() => ctl.setSetting('midiEnabled', true)}>Enable MIDI</button>
    {:else}
      <p class="small muted">Inputs: {ctl.midi.inputs.join(', ') || 'none connected'}</p>
      {#if ctl.midi.clockBpm}<p class="small">
          MIDI clock: <b>{ctl.midi.clockBpm.toFixed(1)} BPM</b>
        </p>{/if}
      {#if ctl.midi.lastMessage}<p class="small muted">Last: {ctl.midi.lastMessage}</p>{/if}
      {#if ctl.midi.learning}<p class="hint capture">
          Learning “{ctl.midi.learning.label}”: move a control…
          <button class="link" onclick={() => ctl.midiLearn(null)}>cancel</button>
        </p>{/if}
      <p class="hint">
        Click <b>M</b> next to any parameter to map a knob. Map pads/keys to actions here:
      </p>
      <div class="row wrap">
        <select id="midi-action" aria-label="Action to map">
          {#each bindings as b (`${b.action}:${b.arg}`)}<option value={`${b.action}|${b.arg ?? ''}`}
              >{b.label}</option
            >{/each}
        </select>
        <button
          onclick={() => {
            const v = (document.getElementById('midi-action') as HTMLSelectElement).value.split(
              '|',
            );
            const arg = v[1] === '' ? undefined : Number(v[1]);
            ctl.midiLearn({
              kind: 'action',
              action: v[0]!,
              ...(arg !== undefined ? { arg } : {}),
              label: describeBinding({ action: v[0]!, ...(arg !== undefined ? { arg } : {}) }),
            });
          }}>Learn pad</button
        >
      </div>
      <div class="list bindings">
        {#each ctl.midi.bindings as b (b.id)}
          <div class="item small">
            <span class="chip">{describeTrigger(b.trigger)}</span>
            <span class="grow">{b.target.label}</span>
            <button
              class="icon-btn"
              aria-label="Remove mapping"
              onclick={() => ctl.midi.unbind(b.id)}><Icon name="close" /></button
            >
          </div>
        {/each}
      </div>
      <button class="link" onclick={() => ctl.setSetting('midiEnabled', false)}>Disable MIDI</button
      >
    {/if}
  </AccordionSection>
{/if}

{#if extraShown('Profile')}
  <AccordionSection
    id="profile"
    title="Profile"
    open={isOpen('Profile')}
    ontoggle={() => toggle('Profile')}
  >
    <div class="row wrap">
      <button onclick={() => ctl.exportSettings()}>Export settings & shortcuts</button>
      <button onclick={() => profileInput?.click()}>Import…</button>
    </div>
    <input
      bind:this={profileInput}
      class="visually-hidden"
      aria-label="Choose files"
      type="file"
      accept=".json"
      onchange={(e) => {
        const f = [...(e.currentTarget.files ?? [])];
        e.currentTarget.value = '';
        void ctl.importFiles(f);
      }}
    />
    <p class="hint">
      Device settings (audio devices, latency, performance) and personal preferences. Presets never
      include these.
    </p>
  </AccordionSection>
{/if}

{#if extraShown('About')}
  <AccordionSection
    id="about"
    title="About"
    open={isOpen('About')}
    ontoggle={() => toggle('About')}
  >
    <p class="hint first">
      Everything runs in this browser: no account, no uploads, no tracking. Built with open-source
      software; see the <a href="THIRD_PARTY_NOTICES.txt" target="_blank" rel="noopener"
        >third-party notices</a
      >.
    </p>
    <p class="hint">
      Free for personal, noncommercial use. Commercial use needs a license: mark@milhouse.family.
    </p>
    <p class="hint" data-testid="about-credit">
      © 2026 Mark Milhouse ·
      <a href="https://markmilhouse.com/" target="_blank" rel="noopener author">markmilhouse.com</a>
    </p>
    <details class="terms" data-testid="about-terms">
      <summary>Terms of use</summary>
      <TermsText />
    </details>
  </AccordionSection>
{/if}

<style>
  .terms summary {
    cursor: pointer;
    margin-bottom: 8px;
    font-size: 13px;
  }
  .setting.child {
    margin-left: 14px;
    padding-left: 10px;
    border-left: 2px solid var(--border);
  }
  .needs {
    color: var(--warn);
  }
  .reset {
    justify-content: flex-end;
    margin-bottom: 6px;
  }
  .search {
    width: 100%;
    margin-bottom: 6px;
  }
  .setting {
    padding: 6px 0;
  }
  .setting-head {
    display: flex;
    align-items: center;
    gap: 6px;
    min-height: 26px;
  }
  .setting-head .label {
    flex: 1;
    font-size: 13px;
  }
  .setting-head select {
    font-size: 12px;
  }
  .setting-head input[type='checkbox'] {
    accent-color: var(--accent);
    width: 16px;
    height: 16px;
  }
  .setting .hint {
    margin-top: 2px;
  }
  .num {
    display: grid;
    grid-template-columns: 1fr 72px;
    gap: 8px;
    align-items: center;
  }
  .num input {
    width: 100%;
    accent-color: var(--accent);
  }
  .v {
    font-family: var(--mono);
    font-size: 11px;
    color: var(--muted);
    text-align: right;
  }
  .keys {
    max-height: 340px;
    overflow-y: auto;
    display: grid;
    gap: 2px;
  }
  .key-row {
    display: flex;
    align-items: center;
    gap: 4px;
    min-height: 26px;
  }
  .kbd {
    border: none;
    background: none;
    padding: 0 2px;
  }
  .capture {
    color: var(--accent);
  }
  .bindings {
    margin: 8px 0;
  }
  #midi-action {
    max-width: 220px;
    font-size: 12px;
  }
</style>
